import { Card, H1, Text } from '@payaca/ambrosia';
import RouterTabs from '@payaca/components/routerTabs/RouterTabs';
import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { FC, type JSX } from 'react';
import { Helmet } from 'react-helmet';

import useGetStockLocation from '@/api/queries/stockLocations/useGetStockLocation';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';

import FractionalCurrencyValue from './components/FractionalCurrencyValue';
import StockLocationPurchaseOrders from './components/StockLocationPurchaseOrders';
import StockLocationStockLevels from './components/StockLocationStockLevels';

type Props = {
  stockLocationId: string;
};

const StockLocationPage: FC<Props> = (props: Props): JSX.Element => {
  const { stockLocationId } = props;

  const { stockLocation } = useGetStockLocation(stockLocationId);

  const panels = [
    {
      slug: 'materials-in-stock',
      label: 'Materials in stock',
      render: () => (
        <StockLocationStockLevels stockLocationId={stockLocationId} />
      ),
    },
    {
      slug: 'purchase-orders',
      label: 'Purchase orders',
      render: () => (
        <StockLocationPurchaseOrders stockLocationId={stockLocationId} />
      ),
    },
  ];

  const addressSummary = stockLocation?.address
    ? getAddressAsString({
        line1: stockLocation.address.line1 ?? undefined,
        line2: stockLocation.address.line2 ?? undefined,
        city: stockLocation.address.city ?? undefined,
        administrativeArea:
          stockLocation.address.administrativeArea ?? undefined,
        postcode: stockLocation.address.postalCode ?? undefined,
      })
    : '';

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Stock locations', to: '/stock-locations' },
        { label: stockLocation?.name ?? '' },
      ]}
    >
      <Helmet title={stockLocation?.name ?? 'Stock location'} />
      <RouterTabs panels={panels}>
        <div className="flex flex-col p-6 pb-4">
          <Card>
            <Card.Body>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <H1>{stockLocation?.name}</H1>
                  {addressSummary && <Text>{addressSummary}</Text>}
                </div>
                {stockLocation && (
                  <div className="text-right">
                    <p className="text-gray-700">Total value (ex tax)</p>
                    <p className="text-lg font-semibold">
                      <FractionalCurrencyValue
                        value={stockLocation.materialsList.stockValueExcTax}
                      />
                    </p>
                  </div>
                )}
              </div>

              <RouterTabs.Nav
                className="-mb-4 md:-mb-5"
                defaultTabSlug="materials-in-stock"
              />
            </Card.Body>
          </Card>
        </div>

        <div className="p-6 pt-0">
          <RouterTabs.Panels />
        </div>
      </RouterTabs>
    </AuthenticatedPageWrapper>
  );
};

export default StockLocationPage;
